/**
 * Enhanced Injector Component with Theme Isolation
 *
 * Used to inject themed content from experience fragments into the DOM
 * while preventing CSS variable and font-face conflicts between themes.
 */
import { loadHtmlPartialScripts } from '../../utils/loadHtmlPartialScripts'

export class GMXInjector extends HTMLElement {
	intersectionObserver: IntersectionObserver
	mutationObserver: MutationObserver
	sharedFragmentObserver: MutationObserver

	private boundHandleObservationCallback = this.handleObservationCallback.bind(this)
	private static globalFontsLoaded = new Set<string>()

	/** Attributes that hold child fragment paths in injected fragments. */
	private static readonly CHILD_FRAGMENT_ATTRIBUTES = [
		'data-content-path',
		'data-panel-reveal-id',
		'data-overlay-reveal-id',
		'data-expanded-content-path',
	]
	private static readonly CHILD_FRAGMENT_SELECTOR = GMXInjector.CHILD_FRAGMENT_ATTRIBUTES.map(
		name => `[${name}]`
	).join(',')
	/** `.frag.html` at the end of the path, before any query string or hash. */
	private static readonly FRAG_HTML = /\.frag\.html(?=[?#]|$)/

	get theme(): string {
		return this.getAttribute('data-theme')
	}
	set theme(val: string) {
		this.setAttribute('data-theme', val)
	}

	connectedCallback(): void {
		const lazyLoad = this.getAttribute('data-lazy-load') === 'true'
		if (lazyLoad) {
			// If lazy loading is enabled, load content when the element is in view
			this.intersectionObserver = new IntersectionObserver(this.boundHandleObservationCallback, {
				root: null,
				rootMargin: '0px',
				threshold: 0,
			})
			this.intersectionObserver.observe(this)
		} else {
			// Load content immediately
			this.loadContent()
		}
	}

	handleObservationCallback(): void {
		this.loadContent()
		if (this.intersectionObserver) {
			this.intersectionObserver.unobserve(this)
		}
	}

	loadContent(): void {
		const fragmentPath = this.getAttribute('data-fragment-url')

		if (fragmentPath) {
			fetch(fragmentPath)
				.then(response => {
					if (!response.ok) {
						// Do nothing if not a successful response
						return null
					}
					return response.text()
				})
				.then(data => {
					if (!data) return
					const shadow = this.attachShadow({ mode: 'open' })
					shadow.innerHTML = '<div class="shadow-wrapper" style="display: none"></div>'
					const shadowWrapper: HTMLElement = shadow.querySelector('.shadow-wrapper')
					// Parse into an inert template so paths are rewritten before child elements connect and fetch
					const template = document.createElement('template')
					template.innerHTML = data
					this.useSharedChildFragments(template.content, fragmentPath)
					shadowWrapper.appendChild(template.content)
					this.observeSharedChildFragments(shadowWrapper, fragmentPath)
					loadHtmlPartialScripts(shadowWrapper, 'high', shadowWrapper)
						.catch(error => {
							console.error('Injector Component Error loading scripts:', error)
						})
						.finally(() => {
							// Fail open: wire singletons even when external scripts fail to load
							this._setupInteractiveComponents(shadow, shadowWrapper)
						})

					// Create Array of stylesheets from the shadow DOM
					const styles: HTMLLinkElement[] = Array.from(
						shadowWrapper.querySelectorAll('link[rel="stylesheet"]')
					)
					// Remove the styles from the shadow DOM to avoid duplication
					styles.forEach(style => {
						style.remove()
					})
					this.loadCSSStyles(styles)
						.then(() => {
							shadowWrapper.style.display = 'block' // Show the content after loading
						})
						.catch(error => {
							console.error('Injector Component Error loading styles:', error)
						})
				})
				.catch(error => {
					console.error('Injector Component Error loading fragment:', error)
				})
		} else {
			console.error('Injector Component No fragment path specified')
		}
	}

	/**
	 * BAPI fragments (*.shared.html) reference child fragments as *.frag.html; request them as *.shared.html instead.
	 */
	useSharedChildFragments(root: ParentNode, fragmentPath: string): void {
		if (!GMXInjector.isSharedFragment(fragmentPath)) return

		root.querySelectorAll(GMXInjector.CHILD_FRAGMENT_SELECTOR).forEach(el => GMXInjector.swapToSharedFragment(el))
	}

	/**
	 * Keeps swapping child fragment paths in content added or changed after the initial render (flyouts, panels, etc.).
	 */
	observeSharedChildFragments(shadowWrapper: HTMLElement, fragmentPath: string): void {
		if (!GMXInjector.isSharedFragment(fragmentPath)) return

		this.sharedFragmentObserver = new MutationObserver(mutations => {
			mutations.forEach(mut => {
				if (mut.type === 'attributes') {
					GMXInjector.swapToSharedFragment(mut.target as Element)
					return
				}
				mut.addedNodes.forEach(node => {
					if (node.nodeType !== Node.ELEMENT_NODE) return
					GMXInjector.swapToSharedFragment(node as Element)
					this.useSharedChildFragments(node as Element, fragmentPath)
				})
			})
		})
		this.sharedFragmentObserver.observe(shadowWrapper, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: GMXInjector.CHILD_FRAGMENT_ATTRIBUTES,
		})
	}

	private static isSharedFragment(fragmentPath: string): boolean {
		return new URL(fragmentPath, document.baseURI).pathname.endsWith('.shared.html')
	}

	private static swapToSharedFragment(el: Element): void {
		for (const name of GMXInjector.CHILD_FRAGMENT_ATTRIBUTES) {
			const value = el.getAttribute(name)
			if (value && GMXInjector.FRAG_HTML.test(value)) {
				el.setAttribute(name, value.replace(GMXInjector.FRAG_HTML, '.shared.html'))
			}
		}
	}

	async loadCSSStyles(styles: HTMLLinkElement[]): Promise<void> {
		if (this.theme) {
			try {
				// Start all fetches in parallel
				const cssPromises = styles.map(style =>
					fetch(style.href)
						.then(r => r.text())
						.then(css => ({
							css,
							url: style.href,
						}))
				)

				// Wait for all to complete
				const cssResults = await Promise.all(cssPromises)

				// Now process and append in order
				for (const { css, url } of cssResults) {
					const processedCSS = this.processCSS(css, url)

					// Add to shadow DOM in order
					const shadowStyle = document.createElement('style')
					const styleKey = `${this.theme}-${url}`
					shadowStyle.setAttribute('data-theme-style', styleKey)
					shadowStyle.innerHTML = processedCSS.shadow
					this.shadowRoot.appendChild(shadowStyle)

					// Add global styles if needed
					if (processedCSS.hasGlobalRules) {
						this.addGlobalStyles(styleKey, processedCSS.global)
					}
				}
			} catch (error) {
				console.error('Injector Component Error loading styles:', error)
				throw error
			}
		}
	}

	fetchStyle = (url: string): Promise<void> => {
		return new Promise((resolve, reject): void => {
			const link: HTMLLinkElement = document.createElement('link')
			link.type = 'text/css'
			link.rel = 'stylesheet'
			link.onload = (): void => {
				resolve()
			}
			link.onerror = (): void => {
				reject(`Error loading stylesheet: ${url}`)
			}
			link.href = url
			this.shadowRoot.appendChild(link)
		})
	}

	/**
	 * Process themed styles with proper isolation
	 */
	processThemedStyle(url: string): Promise<void> {
		return new Promise((resolve, reject): void => {
			try {
				fetch(url)
					.then(response => response.text())
					.then(css => {
						// Process the CSS for theme isolation
						const processedCSS = this.processCSS(css, url)

						// Always add to shadow DOM (including font-face rules)
						const shadowStyle = document.createElement('style')
						const styleKey = `${this.theme}-${url}`
						shadowStyle.setAttribute('data-theme-style', styleKey)
						shadowStyle.innerHTML = processedCSS.shadow
						this.shadowRoot.appendChild(shadowStyle)

						// Only add to document if we have global rules (for elements outside shadow)
						if (processedCSS.hasGlobalRules) {
							this.addGlobalStyles(styleKey, processedCSS.global)
						}

						resolve()
					})
			} catch (error) {
				reject(`Error: Failed to fetch ${url}: ${error}`)
			}
		})
	}

	processCSS(css: string, sourceUrl: string): { shadow: string; global: string; hasGlobalRules: boolean } {
		const themeNamespace = `gmx-injector[data-theme="${this.theme}"]`
		let shadowCSS = css
		let globalCSS = ''
		let hasGlobalRules = false

		// Extract and process @font-face rules
		const fontFaceRegex = /@font-face\s*{[^}]+}/g
		const fontFaces = css.match(fontFaceRegex) || []

		if (fontFaces.length > 0) {
			hasGlobalRules = true

			// Add font-face rules to BOTH global and shadow CSS
			// Global: for elements outside shadow DOM
			// Shadow: for elements inside shadow DOM

			fontFaces.forEach(fontFace => {
				const fontFamily = this.extractFontFamily(fontFace)
				if (fontFamily) {
					// Create a unique key for this font
					const fontKey = `${fontFamily}-${sourceUrl}`

					// Add to global styles if not already loaded
					if (!GMXInjector.globalFontsLoaded.has(fontKey)) {
						globalCSS += fontFace + '\n'
						GMXInjector.globalFontsLoaded.add(fontKey)
					}
				}
			})

			// IMPORTANT: Keep font-face rules in shadow CSS
			// They MUST be in the shadow DOM for fonts to work there
			// Don't remove them from shadowCSS
		}

		// Process CSS custom properties (variables)
		// Replace :root with :host for shadow DOM
		shadowCSS = shadowCSS.replace(/:root/g, ':host')

		// For global CSS, scope :root to theme namespace
		if (css.includes(':root')) {
			hasGlobalRules = true
			const rootRules = this.extractRootRules(css)
			rootRules.forEach(rule => {
				const scopedRule = rule.replace(':root', themeNamespace)
				globalCSS += scopedRule + '\n'
			})
		}

		// Handle :where(:root) patterns
		shadowCSS = shadowCSS.replace(/:where\(:root\)/g, ':host')

		// Process any CSS that needs to cascade to children outside shadow DOM
		const needsGlobalScope = this.detectGlobalScopeNeeds(css)
		if (needsGlobalScope) {
			hasGlobalRules = true
			const scopedGlobalCSS = css
				.replace(/:root/g, themeNamespace)
				.replace(/:where\(:root\)/g, themeNamespace)
				.replace(/@font-face\s*{[^}]+}/g, '') // Remove font-face as we handle separately

			globalCSS += this.scopeSelectorsToTheme(scopedGlobalCSS, themeNamespace)
		}

		// Apply special handling for shadow DOM to inherit fonts properly
		// Remove this since we're keeping fonts in shadow DOM
		// shadowCSS = this.ensureFontInheritance(shadowCSS)

		return {
			shadow: shadowCSS,
			global: globalCSS,
			hasGlobalRules,
		}
	}

	/**
	 * Extract font-family name from @font-face rule
	 */
	extractFontFamily(fontFaceRule: string): string | null {
		const match = fontFaceRule.match(/font-family:\s*['"]?([^'";]+)['"]?/i)
		return match ? match[1].trim() : null
	}

	/**
	 * Extract :root rules from CSS
	 */
	extractRootRules(css: string): string[] {
		const rules: string[] = []
		const rootRegex = /:root\s*{[^}]+}/g
		const matches = css.match(rootRegex) || []
		rules.push(...matches)
		return rules
	}

	/**
	 * Detect if CSS needs global scope (for elements that might render outside shadow DOM)
	 */
	detectGlobalScopeNeeds(css: string): boolean {
		// Check for patterns that indicate global scope needs
		const globalPatterns = [/\.gmx-modal/, /\.gmx-overlay/, /\.gmx-dropdown/, /\.gmx-tooltip/]

		return globalPatterns.some(pattern => pattern.test(css))
	}

	/**
	 * Scope selectors to theme namespace
	 */
	scopeSelectorsToTheme(css: string, namespace: string): string {
		const lines = css.split('\n')
		const processedLines: string[] = []
		let inAtRule = false
		let braceDepth = 0

		lines.forEach(line => {
			const trimmedLine = line.trim()

			// Track brace depth to handle nested rules
			const openBraces = (line.match(/{/g) || []).length
			const closeBraces = (line.match(/}/g) || []).length
			braceDepth += openBraces - closeBraces

			// Handle at-rules
			if (trimmedLine.startsWith('@')) {
				inAtRule = true
				processedLines.push(line)
				return
			}

			if (inAtRule) {
				processedLines.push(line)
				if (braceDepth === 0) {
					inAtRule = false
				}
				return
			}

			// Skip if already namespaced
			if (line.includes(namespace)) {
				processedLines.push(line)
				return
			}

			// Add namespace to selectors (not inside at-rules and at depth 0)
			if (line.includes('{') && !trimmedLine.startsWith('@') && braceDepth === 1) {
				const selector = line.substring(0, line.indexOf('{'))
				const rest = line.substring(line.indexOf('{'))

				// Parse and namespace each selector part
				const namespacedSelector = selector
					.split(',')
					.map(s => {
						const trimmed = s.trim()
						// Don't namespace :root or :host
						if (trimmed === ':root' || trimmed === ':host') {
							return trimmed
						}
						return `${namespace} ${trimmed}`
					})
					.join(', ')

				processedLines.push(`${namespacedSelector}${rest}`)
			} else {
				processedLines.push(line)
			}
		})

		return processedLines.join('\n')
	}

	/**
	 * Add global styles to document head with deduplication
	 */
	addGlobalStyles(styleKey: string, css: string): void {
		// Check if this style already exists in document head
		const existingStyle = document.querySelector(`[data-theme-style="${styleKey}"]`)
		if (!existingStyle && css) {
			const styleElement = document.createElement('style')
			styleElement.setAttribute('data-theme-style', styleKey)
			styleElement.innerHTML = css
			document.head.appendChild(styleElement)
		}
	}

	disconnectedCallback(): void {
		// Clean up observers if they exist
		if (this.intersectionObserver) {
			this.intersectionObserver.disconnect()
		}
		if (this.mutationObserver) {
			this.mutationObserver.disconnect()
		}
		if (this.sharedFragmentObserver) {
			this.sharedFragmentObserver.disconnect()
		}
		// Note: We don't remove global styles here as other injectors might be using them
	}

	private _setupInteractiveComponents(shadow: ShadowRoot, shadowWrapper: HTMLElement): void {
		this._setupPanels(shadow)
		this._setupOverlays(shadow)
		this._setupTabs(shadow)
		this._setupToasts(shadow)
		this._setupDeeplinks(shadow)

		this.mutationObserver = new MutationObserver(mutations => {
			mutations.forEach(mut => {
				const target = mut.target
				if (mut.addedNodes.length > 0) {
					mut.addedNodes.forEach((node: HTMLElement) => {
						if (target && target.nodeType === 1) {
							if (node && node.nodeType === 1) {
								// @ts-expect-error: adding global class to window
								window.gmxToastSingleton._setupLegalBuilderHelper(node)
							}
						}
					})
				}
			})
		})
		this.mutationObserver.observe(shadowWrapper, { childList: true, subtree: true })
	}

	/**
	 * Once the injector has initialized all CSS and JS, need to then setup listeners for any Panels within the
	 * injected content. Also want to prefetch any Panels so consumer does not have to wait.
	 */
	private _setupPanels(shadow: ShadowRoot): void {
		if (window.gmxPanelSingleton) {
			const panels: { id: string | null; type: string | null }[] = []
			shadow.querySelectorAll('[data-panel-reveal-id]').forEach(panelEl => {
				panels.push({
					id: panelEl.getAttribute('data-panel-reveal-id'),
					type: panelEl.getAttribute('data-panel-type'),
				})
			})
			if (panels.length) {
				window.gmxPanelSingleton.prefetchPanelsForInjector(shadow, panels)
			}
			window.gmxPanelSingleton.setupPanelShowListener(shadow)
		} else {
			window.console.error('GMXInjector: gmxPanelSingleton is not available on window')
		}
	}

	/**
	 * Once the injector has initialized all CSS and JS, need to then setup listeners for any Toast within the
	 * injected content.
	 */
	private _setupToasts(shadow: ShadowRoot): void {
		// @ts-expect-error: adding global class to window
		if (window.gmxToastSingleton) {
			// @ts-expect-error: adding global class to window
			window.gmxToastSingleton.setupToastShowListener(shadow)
			// @ts-expect-error: adding global class to window
			window.gmxToastSingleton.setupDisclosures(shadow)
		}
	}

	/**
	 * Once the injector has injected its content, resolve any pending `?gmx-deeplink`
	 * target that was authored inside this injector's shadow DOM. The deeplink
	 * singleton's initial page-load pass only scans the light DOM (`document.body`),
	 * which cannot reach shadow-encapsulated content, so it is re-run scoped to this
	 * shadow root. It is a no-op when no deeplink param is present or the target lives
	 * elsewhere.
	 */
	private _setupDeeplinks(shadow: ShadowRoot): void {
		if (window.gmxDeeplinkSingleton) {
			window.gmxDeeplinkSingleton.setupDeeplinks(shadow)
		}
	}

	/**
	 * Once the injector has initialized all CSS and JS, need to then setup listeners for any Overlays within the
	 * injected content. Also want to prefetch any Overlays so consumer does not have to wait.
	 */
	private _setupOverlays(shadow: ShadowRoot): void {
		// @ts-expect-error: adding global class to window
		if (window.gmxOverlaySingleton) {
			const overlays: { id: string | null; type: string | null }[] = []
			shadow.querySelectorAll('[data-overlay-reveal-id]').forEach(overlayEl => {
				overlays.push({
					id: overlayEl.getAttribute('data-overlay-reveal-id'),
					type: overlayEl.getAttribute('data-overlay-type'),
				})
			})
			if (overlays.length) {
				// @ts-expect-error: adding global class to window
				window.gmxOverlaySingleton.prefetchOverlaysForInjector(shadow, overlays)
			}
			// @ts-expect-error: adding global class to window
			window.gmxOverlaySingleton.setupOverlayShowListener(shadow)
		}
	}

	/**
	 * Once the injector has initialized all CSS and JS, need to then setup listeners for any Tabs within the
	 * injected content. Also need to init any tabs within the injected content.
	 */
	private _setupTabs(shadow: ShadowRoot): void {
		// @ts-expect-error: adding global class to window
		if (window.gmxTabNavigationSingleton) {
			// @ts-expect-error: adding global class to window
			window.gmxTabNavigationSingleton.setupTabNavShowListener(shadow)
			// @ts-expect-error: adding global class to window
			window.gmxTabNavigationSingleton.initTabs(shadow)
		}
	}
}

if (!customElements.get('gmx-injector')) {
	customElements.define('gmx-injector', GMXInjector)
}
